import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import {
  faGripDotsVertical,
  faThumbtack
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { Checkbox } from '~/components/Form/Input/Checkbox'
import {
  columnPickerCheckbox,
  columnPickerDropLine,
  columnPickerDropLineBottom,
  columnPickerDropLineTop,
  columnPickerGrip,
  columnPickerItem,
  columnPickerItemDragging,
  columnPickerItemHidden,
  columnPickerLabel,
  columnPickerPin,
  columnPickerPinActive
} from './DataTable.css'

export type DataTableColumnPin = 'left' | 'right' | false

export type DataTableColumnPickerItem = {
  id: string
  label: string
  visible: boolean
  pin: DataTableColumnPin
  hideable: boolean
}

export type ColumnPickerZone = 'left' | 'middle' | 'right'

export type ColumnPickerDropEdge = 'top' | 'bottom'

const getDropEdge = (
  element: Element,
  clientY: number
): ColumnPickerDropEdge => {
  const rect = element.getBoundingClientRect()
  return clientY < rect.top + rect.height / 2 ? 'top' : 'bottom'
}

type DataTableColumnPickerRowProps = {
  column: DataTableColumnPickerItem
  zone: ColumnPickerZone
  dragDisabled: boolean
  onToggle: (columnId: string) => void
  onPinChange: (columnId: string, pin: DataTableColumnPin) => void
}

const DataTableColumnPickerRow = ({
  column,
  zone,
  dragDisabled,
  onToggle,
  onPinChange
}: DataTableColumnPickerRowProps): ReactElement => {
  const ref = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)
  const [dropEdge, setDropEdge] = useState<ColumnPickerDropEdge | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || dragDisabled) return

    const cleanupDrag = draggable({
      element: el,
      getInitialData: () => ({
        type: 'data-table-column',
        columnId: column.id,
        zone
      }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false)
    })

    const cleanupDrop = dropTargetForElements({
      element: el,
      getData: ({ input }) => ({
        type: 'data-table-column-target',
        columnId: column.id,
        zone,
        edge: getDropEdge(el, input.clientY)
      }),
      canDrop: ({ source }) =>
        source.data.type === 'data-table-column' &&
        source.data.columnId !== column.id,
      onDragEnter: ({ self }) => {
        const edge = self.data.edge
        if (edge === 'top' || edge === 'bottom') setDropEdge(edge)
      },
      onDrag: ({ self }) => {
        const edge = self.data.edge
        if (edge === 'top' || edge === 'bottom') setDropEdge(edge)
      },
      onDragLeave: () => setDropEdge(null),
      onDrop: () => setDropEdge(null)
    })

    return () => {
      cleanupDrag()
      cleanupDrop()
    }
  }, [column.id, dragDisabled, zone])

  return (
    <div
      ref={ref}
      className={clsx(
        columnPickerItem,
        !column.visible && columnPickerItemHidden,
        dragging && columnPickerItemDragging
      )}
    >
      {dropEdge === 'top' && (
        <div
          className={clsx(columnPickerDropLine, columnPickerDropLineTop)}
          aria-hidden
        />
      )}
      <span className={columnPickerGrip} aria-hidden>
        <FontAwesomeIcon icon={faGripDotsVertical} />
      </span>
      <div className={columnPickerLabel}>
        <div className={columnPickerCheckbox}>
          <Checkbox
            checked={column.visible}
            disabled={!column.hideable}
            onChange={() => {
              if (column.hideable) onToggle(column.id)
            }}
            label={column.label}
            colorPalette="medium"
          />
        </div>
      </div>
      <button
        type="button"
        className={clsx(
          columnPickerPin,
          column.pin === 'left' && columnPickerPinActive
        )}
        title="Épingler à gauche"
        aria-pressed={column.pin === 'left'}
        onClick={() =>
          onPinChange(column.id, column.pin === 'left' ? false : 'left')
        }
      >
        <FontAwesomeIcon icon={faThumbtack} rotation={270} />
      </button>
      <button
        type="button"
        className={clsx(
          columnPickerPin,
          column.pin === 'right' && columnPickerPinActive
        )}
        title="Épingler à droite"
        aria-pressed={column.pin === 'right'}
        onClick={() =>
          onPinChange(column.id, column.pin === 'right' ? false : 'right')
        }
      >
        <FontAwesomeIcon icon={faThumbtack} rotation={90} />
      </button>
      {dropEdge === 'bottom' && (
        <div
          className={clsx(columnPickerDropLine, columnPickerDropLineBottom)}
          aria-hidden
        />
      )}
    </div>
  )
}

export default DataTableColumnPickerRow
